import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  countManuscriptWords,
} from "../utils/childrensBookManuscriptCapacity";

import {
  MANUSCRIPT_PAGE_MAXIMUM_WORDS,
  MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS,
  buildChildrensBookManuscriptUnitDivisionProposal,
  suggestChildrensBookManuscriptUnitDivision,
} from "../utils/childrensBookManuscriptUnitDivision";

function clean(value) {
  return String(value ?? "").trim();
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function physicalPageSpan(
  unit = {}
) {
  return unit?.unitType ===
    "double_page_spread"
    ? 2
    : 1;
}

function divisionDestinationLabel(
  segment,
  isFR
) {
  const pageStart =
    Number(
      segment?.pageStart
    );

  const pageEnd =
    Number(
      segment?.pageEnd
    );

  if (
    !Number.isInteger(
      pageStart
    ) ||
    !Number.isInteger(
      pageEnd
    )
  ) {
    return "";
  }

  if (
    segment?.unitType ===
      "double_page_spread" ||
    pageEnd > pageStart
  ) {
    return isFR
      ? `Double page — Pages ${pageStart}–${pageEnd}`
      : `Two-page Spread — Pages ${pageStart}–${pageEnd}`;
  }

  return isFR
    ? `Page simple — Page ${pageStart}`
    : `Single Page — Page ${pageStart}`;
}

export default function ChildrensBookManuscriptUnitDivisionReview({
  lang = "en",
  projectPlan = {},
  sourceUnitId = "",
  childrensBookAssets = [],
  saving = false,
  onApply,
  onClose,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const assets =
    useMemo(
      () =>
        safeArray(
          childrensBookAssets
        ),
      [
        childrensBookAssets,
      ]
    );

  const sourceUnit =
    useMemo(
      () =>
        safeArray(
          projectPlan.bookMap
            ?.units
        ).find(
          (unit) =>
            clean(unit?.id) ===
            clean(sourceUnitId)
        ) || null,
      [
        projectPlan.bookMap
          ?.units,
        sourceUnitId,
      ]
    );

  const sourceManuscriptEntry =
    safeArray(
      projectPlan.manuscriptPlan
        ?.entries
    ).find(
      (entry) =>
        clean(entry?.unitId) ===
        clean(sourceUnitId)
    ) || null;

  const sourceIllustrationEntries =
    safeArray(
      projectPlan.illustrationPlan
        ?.entries
    ).filter(
      (entry) =>
        clean(entry?.unitId) ===
        clean(sourceUnitId)
    );

  const sourceAssets =
    assets.filter(
      (asset) =>
        clean(
          asset?.bookMapUnitId
        ) ===
        clean(sourceUnitId)
    );

  const suggestionResetKey =
    JSON.stringify({
      sourceUnitId:
        clean(sourceUnitId),

      sourceUnit,
      sourceManuscriptEntry,
      sourceIllustrationEntries,
      sourceAssets,
    });

  const suggestion =
    useMemo(
      () =>
        suggestChildrensBookManuscriptUnitDivision({
          projectPlan,
          sourceUnitId,
          childrensBookAssets:
            assets,
        }),
      [
        projectPlan,
        sourceUnitId,
        assets,
      ]
    );

  const [
    segments,
    setSegments,
  ] = useState([]);

  const [
    unitTypes,
    setUnitTypes,
  ] = useState([]);

  const [
    illustrationSegmentIndex,
    setIllustrationSegmentIndex,
  ] = useState(null);

  const [
    activeSegmentIndex,
    setActiveSegmentIndex,
  ] = useState(0);

  const [
    applicationConfirmationOpen,
    setApplicationConfirmationOpen,
  ] = useState(false);

  const [
    applyingDivision,
    setApplyingDivision,
  ] = useState(false);

  const [
    applicationError,
    setApplicationError,
  ] = useState("");

  const initializedSuggestionKeyRef =
    useRef("");

  useEffect(() => {
    if (
      initializedSuggestionKeyRef
        .current ===
      suggestionResetKey
    ) {
      return;
    }

    initializedSuggestionKeyRef.current =
      suggestionResetKey;
    setSegments(
      safeArray(
        suggestion?.suggestion
          ?.segments
      )
    );

    setUnitTypes(
      safeArray(
        suggestion?.suggestion
          ?.segments
      ).map(
        () =>
          "single_page"
      )
    );

    setIllustrationSegmentIndex(
      null
    );

    setActiveSegmentIndex(
      0
    );

    setApplicationConfirmationOpen(
      false
    );

    setApplyingDivision(
      false
    );

    setApplicationError(
      ""
    );
  }, [
    suggestion,
    suggestionResetKey,
  ]);

  const proposal =
    useMemo(
      () => {
        if (
          segments.length < 2 ||
          unitTypes.length !==
            segments.length
        ) {
          return {
            ok: false,
            readyToApply: false,
            error:
              "The division review is incomplete.",
          };
        }

        return buildChildrensBookManuscriptUnitDivisionProposal({
          projectPlan,
          sourceUnitId,
          segments,
          unitTypes,

          illustrationSegmentIndex:
            Number.isInteger(
              illustrationSegmentIndex
            )
              ? illustrationSegmentIndex
              : undefined,

          childrensBookAssets:
            assets,
        });
      },
      [
        projectPlan,
        sourceUnitId,
        segments,
        unitTypes,
        illustrationSegmentIndex,
        assets,
      ]
    );

  const reviewProposedSegments =
    proposal.ok
      ? safeArray(
          proposal
            .proposedSegments
        )
      : safeArray(
          suggestion
            .proposedSegments
        );

  const pageCount =
    segments.length;

  const firstPhysicalPageNumber =
    (() => {
      const firstDescriptor =
        reviewProposedSegments[0];

      const explicitStart =
        Number(
          firstDescriptor
            ?.pageStart
        );

      if (
        Number.isFinite(
          explicitStart
        ) &&
        explicitStart > 0
      ) {
        return explicitStart;
      }

      const firstNumberMatch =
        clean(
          firstDescriptor
            ?.pageNumber
        ).match(/\d+/);

      return firstNumberMatch
        ? Number(
            firstNumberMatch[0]
          )
        : 1;
    })();

  const reviewNextBookMapUnits =
    safeArray(
      (
        proposal.ok
          ? proposal.nextBookMap
          : suggestion.nextBookMap
      )?.units
    );

  const resultingPhysicalPageCount =
    reviewNextBookMapUnits.reduce(
      (
        total,
        unit
      ) =>
        total +
        physicalPageSpan(
          unit
        ),
      0
    );

  const pageViews =
    (() => {
      const views = [];

      for (
        let pageIndex = 0;
        pageIndex < pageCount;
        pageIndex += 1
      ) {
        const pageNumber =
          firstPhysicalPageNumber +
          pageIndex;

        const partNumber =
          Math.floor(
            (
              pageNumber - 1
            ) / 2
          ) + 1;

        let view =
          views[
            views.length - 1
          ];

        if (
          !view ||
          view.partNumber !==
            partNumber
        ) {
          const partStartPage =
            (
              partNumber - 1
            ) * 2 + 1;

          const naturalPartEndPage =
            partStartPage + 1;

          const partEndPage =
            resultingPhysicalPageCount > 0
              ? Math.min(
                  naturalPartEndPage,
                  resultingPhysicalPageCount
                )
              : naturalPartEndPage;

          const pagesLabel =
            partEndPage >
            partStartPage
              ? `Pages ${partStartPage}–${partEndPage}`
              : `Page ${partStartPage}`;

          view = {
            viewIndex:
              views.length,
            partNumber,
            pageIndices: [],
            pageNumbers: [],
            pagesLabel,

            label:
              isFR
                ? `Partie ${partNumber} — ${pagesLabel}`
                : `Part ${partNumber} — ${pagesLabel}`,
          };

          views.push(
            view
          );
        }

        view.pageIndices.push(
          pageIndex
        );

        view.pageNumbers.push(
          String(
            pageNumber
          )
        );
      }

      return views;
    })();

  const maximumPageViewIndex =
    Math.max(
      pageViews.length - 1,
      0
    );

  const boundedActiveSegmentIndex =
    Math.max(
      0,
      Math.min(
        activeSegmentIndex,
        Math.max(
          pageCount - 1,
          0
        )
      )
    );

  const matchedPageViewIndex =
    pageViews.findIndex(
      (view) =>
        view.pageIndices.includes(
          boundedActiveSegmentIndex
        )
    );

  const activePageViewIndex =
    matchedPageViewIndex >= 0
      ? matchedPageViewIndex
      : 0;

  const activePageView =
    pageViews[
      activePageViewIndex
    ] || null;

  const activePartWordCount =
    activePageView
      ? activePageView
          .pageIndices
          .reduce(
            (
              total,
              pageIndex
            ) =>
              total +
              countManuscriptWords(
                segments[
                  pageIndex
                ] || ""
              ),
            0
          )
      : 0;

  const activePartWordLimit =
    activePageView
      ? activePageView
          .pageIndices
          .length *
          MANUSCRIPT_PAGE_MAXIMUM_WORDS
      : 0;

  if (
    !sourceUnit ||
    !suggestion
  ) {
    return null;
  }

  if (
    !suggestion.ok ||
    suggestion.divisionNeeded ===
      false
  ) {
    const suggestionFailed =
      !suggestion.ok;

    return (
      <section
        className="ns-card ns-nbb-major-panel"
        role="dialog"
        aria-modal="false"
        style={{
          padding: 20,
          borderRadius: 18,
          marginTop: 18,
        }}
      >
        <strong>
          {suggestionFailed
            ? (
                isFR
                  ? "La révision de la division n’a pas pu être préparée."
                  : "Division Review Could Not Be Prepared"
              )
            : (
                isFR
                  ? "Cette unité n’a pas besoin d’être divisée."
                  : "This unit does not need to be divided."
              )}
        </strong>

        {suggestionFailed ? (
          <p
            role="alert"
            className="ns-card-meta"
            style={{
              margin:
                "10px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Vérifiez que cette unité contient un manuscrit valide, puis réessayez."
              : (
                  suggestion.error ||
                  "Check that this unit contains a valid manuscript, then try again."
                )}
          </p>
        ) : null}

        <div
          style={{
            marginTop: 14,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onClose}
          >
            {isFR
              ? "Fermer"
              : "Close"}
          </button>
        </div>
      </section>
    );
  }

  const applicationEnabled =
    typeof onApply ===
    "function";

  const capacityRequirements =
    proposal.ok
      ? safeArray(
          proposal.requirements
        ).filter(
          (requirement) =>
            requirement?.type ===
            "segment_capacity"
        )
      : [];

  const automaticSegments =
    safeArray(
      suggestion?.suggestion
        ?.segments
    ).map(
      (segment) =>
        String(segment || "")
    );

  const automaticBalanceAvailable =
    capacityRequirements.length > 0 &&
    automaticSegments.length >= 2 &&
    automaticSegments.length ===
      segments.length;

  const divisionBusy =
    applyingDivision ||
    Boolean(saving);

  const selectedIllustrationSegment =
    Number.isInteger(
      illustrationSegmentIndex
    )
      ? safeArray(
          proposal
            .proposedSegments
        )[
          illustrationSegmentIndex
        ]
      : null;

  const selectedIllustrationDestination =
    selectedIllustrationSegment
      ? divisionDestinationLabel(
          selectedIllustrationSegment,
          isFR
        )
      : "";

  function restoreBalancedProposal() {
    if (
      !automaticBalanceAvailable ||
      divisionBusy
    ) {
      return;
    }

    setApplicationConfirmationOpen(
      false
    );

    setApplicationError(
      ""
    );

    setSegments(
      automaticSegments
    );

    setUnitTypes(
      automaticSegments.map(
        () =>
          "single_page"
      )
    );
  }

  async function confirmDivisionApplication(
    event
  ) {
    event.preventDefault();

    if (
      !applicationEnabled ||
      !proposal.ok ||
      !proposal.readyToApply ||
      divisionBusy
    ) {
      return;
    }

    setApplyingDivision(
      true
    );

    setApplicationError(
      ""
    );

    let applicationCompleted =
      false;

    try {
      const result =
        await onApply({
          sourceUnitId:
            clean(
              sourceUnitId
            ),

          segments:
            segments.map(
              (segment) =>
                String(
                  segment ||
                  ""
                )
            ),

          unitTypes:
            unitTypes.map(
              (unitType) =>
                clean(
                  unitType
                )
            ),

          illustrationSegmentIndex:
            Number.isInteger(
              illustrationSegmentIndex
            )
              ? illustrationSegmentIndex
              : undefined,
        });

      if (!result?.ok) {
        const conflict =
          Number(
            result?.status ||
            0
          ) === 409;

        setApplicationError(
          conflict
            ? (
                isFR
                  ? "Ce projet a changé dans une autre session. Rouvrez le projet, vérifiez la version la plus récente, puis réessayez."
                  : "This project changed in another session. Reopen the project, review the latest version, and try again."
              )
            : (
                clean(
                  result?.error
                ) ||
                (
                  isFR
                    ? "La division n’a pas pu être enregistrée. Aucune modification n’a été appliquée."
                    : "The division could not be saved. No changes were applied."
                )
              )
        );

        return;
      }

      applicationCompleted =
        true;

      setApplyingDivision(
        false
      );

      setApplicationConfirmationOpen(
        false
      );

      if (
        typeof onClose ===
        "function"
      ) {
        onClose({
          applied: true,
          result,
        });
      }
    } catch (error) {
      setApplicationError(
        clean(
          error?.message
        ) ||
        (
          isFR
            ? "La division n’a pas pu être enregistrée. Aucune modification n’a été appliquée."
            : "The division could not be saved. No changes were applied."
        )
      );
    } finally {
      if (!applicationCompleted) {
        setApplyingDivision(
          false
        );
      }
    }
  }

  const sourceWordCount =
    Number(
      suggestion.suggestion
        ?.sourceWordCount ||
      suggestion.source
        ?.wordCount ||
      0
    );

  const illustrationContext =
    suggestion
      .illustrationContext;

  const structuredSourceReview =
    suggestion.summary
      ?.sourceContentMode ===
      "structured" ||
    suggestion.suggestion
      ?.sourceContentMode ===
      "structured";

  return (
    <section
      className="ns-card ns-nbb-major-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="manuscript-division-review-title"
      style={{
        padding:
          "clamp(18px, 3vw, 26px)",
        borderRadius: 20,
        marginTop: 18,
        border:
          "1px solid rgba(234,88,12,0.34)",
        background: "#fffaf5",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            {isFR
              ? "Révision de la structure"
              : "Book Structure review"}
          </div>

          <h2
            id="manuscript-division-review-title"
            className="ns-section-title"
            style={{
              margin:
                "5px 0 8px",
            }}
          >
            {isFR
              ? "Réviser les pages proposées"
              : "Review Proposed Pages"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 760,
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? `Cette unité contient ${sourceWordCount} mots. Vérifiez chaque page proposée avant toute modification du livre.`
              : `This unit contains ${sourceWordCount} words. Review each proposed page before anything changes in your book.`}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            divisionBusy
          }
          onClick={onClose}
        >
          {isFR
            ? "Fermer sans appliquer"
            : "Close Without Applying"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 12,
          marginTop: 18,
        }}
      >
        {[
          [
            isFR
              ? "Mots actuels"
              : "Current words",
            sourceWordCount,
          ],
          [
            isFR
              ? "Pages proposées"
              : "Proposed pages",
            segments.length,
          ],
          [
            isFR
              ? "Vues en vis-à-vis"
              : "Facing-page views",
            pageViews.length,
          ],
        ].map(
          ([label, value]) => (
            <div
              key={label}
              className="ns-card ns-nbb-summary-card"
              style={{
                padding: 14,
                borderRadius: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "#64748b",
                }}
              >
                {label}
              </div>

              <div
                style={{
                  marginTop: 5,
                  fontSize: 23,
                  fontWeight: 900,
                }}
              >
                {value}
              </div>
            </div>
          )
        )}
      </div>

      {structuredSourceReview ? (
        <div
          data-testid="division-formatting-safety"
          role="status"
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 12,
            lineHeight: 1.55,
            background: "#eff6ff",
            border:
              "1px solid rgba(37,99,235,0.28)",
            color: "#1e3a8a",
          }}
        >
          <strong>
            {isFR
              ? "Proposition respectueuse de la mise en forme"
              : "Formatting-safe proposal"}
          </strong>

          <div
            style={{
              marginTop: 5,
            }}
          >
            {isFR
              ? "Les titres, listes, citations, alignements, retraits et styles de texte restent attachés aux mots d’origine. Les zones de texte servent uniquement à déplacer les limites entre les pages proposées."
              : "Headings, lists, blockquotes, alignment, indentation, and text styling remain attached to the original words. The text boxes only move boundaries between proposed pages."}
          </div>
        </div>
      ) : null}

      <div
        role={
          proposal.ok
            ? "status"
            : "alert"
        }
        style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 12,
          lineHeight: 1.55,
          background:
            proposal.readyToApply
              ? "#f0fdf4"
              : "#fff7ed",
          border:
            proposal.readyToApply
              ? "1px solid rgba(22,101,52,0.28)"
              : "1px solid rgba(234,88,12,0.30)",
          color:
            proposal.readyToApply
              ? "#166534"
              : "#9a3412",
        }}
      >
        <div>
          {proposal.ok
            ? (
                proposal.readyToApply
                  ? (
                      isFR
                        ? `Toutes les pages proposées respectent la limite de ${MANUSCRIPT_PAGE_MAXIMUM_WORDS} mots et le texte complet${structuredSourceReview ? " ainsi que sa mise en forme" : ""} est préservé.`
                        : `All proposed pages fit the ${MANUSCRIPT_PAGE_MAXIMUM_WORDS}-word page limit and the complete manuscript text${structuredSourceReview ? " and formatting" : ""} is preserved.`
                    )
                  : (
                      isFR
                        ? "La proposition nécessite encore une décision ou une correction."
                        : "The proposal still needs a decision or correction."
                    )
              )
            : (
                isFR
                  ? "Le texte proposé ne préserve pas encore tous les mots, les limites obligatoires ou leur ordre d’origine."
                  : (
                      proposal.error ||
                      "The proposed text does not yet preserve every word, required boundary, and original order."
                    )
              )}
        </div>

        {automaticBalanceAvailable ? (
          <div
            data-testid="division-automatic-balance"
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span>
              {isFR
                ? "Une ou plusieurs pages dépassent la limite. Rétablissez la proposition équilibrée pour corriger automatiquement les limites de page sans perdre ni réordonner les mots."
                : "One or more pages are over the limit. Restore the balanced proposal to correct the page boundaries automatically without losing or reordering words."}
            </span>

            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={divisionBusy}
              onClick={restoreBalancedProposal}
            >
              {isFR
                ? "Rétablir la proposition équilibrée"
                : "Restore Balanced Proposal"}
            </button>
          </div>
        ) : null}
      </div>

      <div
        role="group"
        aria-label={
          isFR
            ? "Navigation des pages proposées"
            : "Proposed-page navigation"
        }
        style={{
          display: "grid",
          gridTemplateColumns:
            "auto minmax(180px, 1fr) auto",
          gap: 12,
          alignItems: "center",
          marginTop: 18,
          padding: 14,
          borderRadius: 14,
          border:
            "1px solid #cbd5e1",
          background: "#ffffff",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            activePageViewIndex ===
            0
          }
          onClick={() => {
            const previousView =
              pageViews[
                Math.max(
                  0,
                  activePageViewIndex -
                    1
                )
              ];

            setActiveSegmentIndex(
              previousView
                ?.pageIndices[0] ||
                0
            );
          }}
        >
          {isFR
            ? "Vue précédente"
            : "Previous View"}
        </button>

        <div
          style={{
            display: "grid",
            gap: 7,
            textAlign: "center",
          }}
        >
          <strong
            data-testid="division-part-heading"
          >
            {activePageView
              ? activePageView.label
              : "—"}
          </strong>

          {activePageView ? (
            <span
              className="ns-card-meta"
              data-testid="division-part-word-total"
              style={{
                fontWeight: 700,
              }}
            >
              {isFR
                ? `Total de la partie : ${activePartWordCount} / ${activePartWordLimit} mots`
                : `Part total: ${activePartWordCount} / ${activePartWordLimit} words`}
            </span>
          ) : null}

          <label
            style={{
              display: "grid",
              gap: 5,
              fontWeight: 700,
            }}
          >
            <span
              className="ns-card-meta"
            >
              {isFR
                ? "Aller directement à des pages"
                : "Jump directly to pages"}
            </span>

            <select
              aria-label={
                isFR
                  ? "Aller à la vue des pages"
                  : "Jump to page view"
              }
              value={
                activePageViewIndex
              }
              onChange={(event) => {
                const requestedView =
                  pageViews[
                    Number(
                      event.target.value
                    )
                  ];

                setActiveSegmentIndex(
                  requestedView
                    ?.pageIndices[0] ||
                    0
                );
              }}
              style={{
                minHeight: 42,
                padding:
                  "9px 11px",
                borderRadius: 10,
                border:
                  "1px solid #cbd5e1",
                background: "#fff",
                font: "inherit",
              }}
            >
              {pageViews.map(
                (view) => (
                  <option
                    key={
                      view.viewIndex
                    }
                    value={
                      view.viewIndex
                    }
                  >
                    {view.label}
                  </option>
                )
              )}
            </select>
          </label>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            activePageViewIndex ===
            maximumPageViewIndex
          }
          onClick={() => {
            const nextView =
              pageViews[
                Math.min(
                  maximumPageViewIndex,
                  activePageViewIndex +
                    1
                )
              ];

            setActiveSegmentIndex(
              nextView
                ?.pageIndices[0] ||
                Math.max(
                  pageCount - 1,
                  0
                )
            );
          }}
        >
          {isFR
            ? "Vue suivante"
            : "Next View"}
        </button>
      </div>

      {activePageView ? (
        <section
          role="group"
          aria-label={
            isFR
              ? `Aperçu des ${activePageView.label.toLowerCase()}`
              : `${activePageView.label} preview`
          }
          style={{
            display: "grid",
            gridTemplateColumns:
              activePageView
                .pageIndices
                .length > 1
                ? "repeat(2, minmax(0, 1fr))"
                : "minmax(0, 560px)",
            gap: 18,
            alignItems: "stretch",
            marginTop: 18,
            maxWidth: 1120,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {activePageView
            .pageIndices
            .map(
              (pageIndex) => {
                const descriptor =
                  reviewProposedSegments[
                    pageIndex
                  ] || null;

                const pagePosition =
                  activePageView
                    .pageIndices
                    .indexOf(
                      pageIndex
                    );

                const pageNumber =
                  activePageView
                    .pageNumbers[
                      pagePosition
                    ] ||
                  String(
                    firstPhysicalPageNumber +
                      pageIndex
                  );

                const pageText =
                  segments[
                    pageIndex
                  ] || "";

                const wordCount =
                  countManuscriptWords(
                    pageText
                  );

                const wordLimit =
                  MANUSCRIPT_PAGE_MAXIMUM_WORDS;

                const preferredMinimum =
                  MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS;

                const oversized =
                  wordCount >
                  wordLimit;

                const belowPreferred =
                  pageCount > 1 &&
                  wordCount <
                  preferredMinimum;

                const rawWorkingTitle =
                  clean(
                    descriptor
                      ?.unit
                      ?.workingTitle
                  );

                const workingTitle =
                  rawWorkingTitle;

                const purpose =
                  clean(
                    descriptor
                      ?.unit
                      ?.purpose
                  );

                const inheritedPlanningDetails =
                  Boolean(
                    descriptor
                      ?.retainsSourceMetadata &&
                    (
                      workingTitle ||
                      purpose
                    )
                  );

                return (
                  <article
                    key={pageIndex}
                    data-testid="division-page-panel"
                    aria-label={
                      `Page ${pageNumber}`
                    }
                    style={{
                      display: "flex",
                      flexDirection:
                        "column",
                      minHeight: 650,
                      padding: 20,
                      borderRadius: 14,
                      border:
                        oversized
                          ? "1px solid rgba(185,28,28,0.45)"
                          : "1px solid #94a3b8",
                      background:
                        "#ffffff",
                      boxShadow:
                        "0 12px 28px rgba(15,23,42,0.10)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "flex-start",
                        gap: 12,
                      }}
                    >
                      <strong
                        style={{
                          fontSize: 22,
                        }}
                      >
                        {`Page ${pageNumber}`}
                      </strong>

                      <span
                        className="ns-card-meta"
                        style={{
                          fontWeight: 700,
                        }}
                      >
                        {isFR
                          ? `${wordCount} mot(s)`
                          : `${wordCount} word(s)`}
                      </span>
                    </div>

                    {inheritedPlanningDetails ? (
                      <section
                        data-testid="division-inherited-planning-details"
                        style={{
                          marginTop: 12,
                          padding: 12,
                          borderRadius: 10,
                          border:
                            "1px solid rgba(59,130,246,0.24)",
                          background:
                            "#f8fbff",
                        }}
                      >
                        <strong
                          style={{
                            display: "block",
                            color: "#1e3a8a",
                          }}
                        >
                          {isFR
                            ? "Détails de planification hérités de la page ou double page d’origine"
                            : "Planning details inherited from the original page or spread"}
                        </strong>

                        <p
                          className="ns-card-meta"
                          style={{
                            margin: "5px 0 0",
                            lineHeight: 1.45,
                          }}
                        >
                          {isFR
                            ? "Ces détails guident la structure du livre et ne font pas partie du texte du manuscrit."
                            : "These details guide the Book Structure and are not part of the manuscript text."}
                        </p>

                        {workingTitle ? (
                          <div
                            style={{
                              marginTop: 10,
                            }}
                          >
                            <span
                              className="ns-card-meta"
                              style={{
                                display:
                                  "block",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {isFR
                                ? "Titre de travail"
                                : "Working title"}
                            </span>

                            <strong>
                              {workingTitle}
                            </strong>
                          </div>
                        ) : null}

                        {purpose ? (
                          <div
                            style={{
                              marginTop: 10,
                            }}
                          >
                            <span
                              className="ns-card-meta"
                              style={{
                                display:
                                  "block",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {isFR
                                ? "Fonction de la page"
                                : "Page purpose"}
                            </span>

                            <span>
                              {purpose}
                            </span>
                          </div>
                        ) : null}
                      </section>
                    ) : null}

                    <label
                      style={{
                        display: "flex",
                        flexDirection:
                          "column",
                        flex: 1,
                        gap: 8,
                        marginTop: 16,
                        fontWeight: 800,
                      }}
                    >
                      <span>
                        {isFR
                          ? `Texte pour la page ${pageNumber}`
                          : `Text for Page ${pageNumber}`}
                      </span>

                      <textarea
                        data-testid="division-page-text-editor"
                        aria-label={
                          isFR
                            ? `Texte pour la page ${pageNumber}`
                            : `Text for Page ${pageNumber}`
                        }
                        rows={20}
                        value={
                          pageText
                        }
                        onChange={(event) => {
                          setApplicationConfirmationOpen(
                            false
                          );

                          setApplicationError(
                            ""
                          );

                          setSegments(
                            (current) =>
                              current.map(
                                (
                                  value,
                                  index
                                ) =>
                                  index ===
                                  pageIndex
                                    ? event
                                        .target
                                        .value
                                    : value
                              )
                          );
                        }}
                        style={{
                          width: "100%",
                          flex: 1,
                          minHeight: 410,
                          boxSizing:
                            "border-box",
                          padding: 14,
                          borderRadius: 10,
                          border:
                            oversized
                              ? "1px solid #dc2626"
                              : "1px solid #cbd5e1",
                          background:
                            "#ffffff",
                          font: "inherit",
                          resize: "vertical",
                          lineHeight: 1.6,
                        }}
                      />
                    </label>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin:
                          "12px 0 0",
                        lineHeight: 1.45,
                      }}
                    >
                      {oversized
                        ? (
                            isFR
                              ? `${wordCount} mots dépassent la limite de ${wordLimit} mots pour cette page.`
                              : `${wordCount} words exceed the ${wordLimit}-word limit for this page.`
                          )
                        : belowPreferred
                          ? (
                              isFR
                                ? `La fourchette habituelle est de ${preferredMinimum} à ${wordLimit} mots. Une page plus courte peut convenir à un titre, un chapitre, une annexe ou une autre page de soutien.`
                                : `The usual range is ${preferredMinimum}–${wordLimit} words. A shorter page may be appropriate for a title, chapter opening, appendix, or other support page.`
                            )
                          : (
                              isFR
                                ? `Fourchette habituelle : ${preferredMinimum} à ${wordLimit} mots.`
                                : `Usual range: ${preferredMinimum}–${wordLimit} words.`
                            )}
                    </p>
                  </article>
                );
              }
            )}
        </section>
      ) : null}

      <p
        className="ns-card-meta"
        style={{
          margin:
            "16px auto 0",
          maxWidth: 900,
          textAlign: "center",
          lineHeight: 1.55,
        }}
      >
        {isFR
          ? "Chaque page possède son propre texte. Déplacez les limites en coupant et collant uniquement entre des pages voisines. Aucun mot ne doit disparaître ni changer d’ordre."
          : "Each page owns its own text. Move boundaries by cutting and pasting only between neighbouring pages. No word may be lost or reordered."}
      </p>

      {illustrationContext
        ?.hasPlanningContext ? (
        <fieldset
          style={{
            marginTop: 18,
            padding: 18,
            borderRadius: 16,
            border:
              "1px solid rgba(59,130,246,0.28)",
            background: "#eff6ff",
          }}
        >
          <legend
            style={{
              fontWeight: 900,
              color: "#1e3a8a",
            }}
          >
            {isFR
              ? "Choisir où conserver l’illustration existante"
              : "Choose Where the Existing Illustration Stays"}
          </legend>

          <p
            className="ns-card-meta"
            style={{
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Sélectionnez la page physique qui doit conserver l’illustration actuelle et ses instructions."
              : "Select the physical page that should keep the current illustration and its directions."}
          </p>

          <label
            style={{
              display: "grid",
              gap: 7,
              fontWeight: 800,
            }}
          >
            <span>
              {isFR
                ? "Destination de l’illustration actuelle"
                : "Current illustration destination"}
            </span>

            <select
              aria-label={
                isFR
                  ? "Destination de l’illustration"
                  : "Illustration destination"
              }
              value={
                Number.isInteger(
                  illustrationSegmentIndex
                )
                  ? String(
                      illustrationSegmentIndex
                    )
                  : ""
              }
              onChange={(event) => {
                const value =
                  event.target.value;

                setApplicationConfirmationOpen(
                  false
                );

                setApplicationError(
                  ""
                );

                setIllustrationSegmentIndex(
                  value === ""
                    ? null
                    : Number(value)
                );
              }}
              style={{
                minHeight: 44,
                padding:
                  "10px 12px",
                borderRadius: 10,
                border:
                  "1px solid #93c5fd",
                background: "#fff",
                font: "inherit",
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir une page"
                  : "Choose a page"}
              </option>

              {segments.map(
                (
                  segment,
                  index
                ) => {
                  const option =
                    proposal.ok
                      ? proposal
                          .proposedSegments[
                            index
                          ]
                      : null;

                  return (
                    <option
                      key={index}
                      value={index}
                    >
                      {divisionDestinationLabel(
                          option,
                          isFR
                        ) ||
                        (
                          isFR
                            ? `Page simple — Page ${firstPhysicalPageNumber + index}`
                            : `Single Page — Page ${firstPhysicalPageNumber + index}`
                        )}
                      {" · "}
                      {countManuscriptWords(
                        segment
                      )}
                      {" "}
                      {isFR
                        ? "mot(s)"
                        : "word(s)"}
                    </option>
                  );
                }
              )}
            </select>
          </label>

          {Number.isInteger(
            illustrationSegmentIndex
          ) ? (
            <p
              role="status"
              style={{
                margin:
                  "12px 0 0",
                fontWeight: 800,
                color: "#1e3a8a",
              }}
            >
              {isFR
                ? `L’illustration actuelle restera avec ${
                    proposal.ok
                      ? divisionDestinationLabel(
                          proposal
                            .proposedSegments[
                              illustrationSegmentIndex
                            ],
                          isFR
                        )
                      : `la page ${firstPhysicalPageNumber + illustrationSegmentIndex}`
                  }.`
                : `The current illustration will stay with ${
                    proposal.ok
                      ? divisionDestinationLabel(
                          proposal
                            .proposedSegments[
                              illustrationSegmentIndex
                            ],
                          isFR
                        )
                      : `Page ${firstPhysicalPageNumber + illustrationSegmentIndex}`
                  }.`}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {applicationEnabled ? (
        <div
          className="ns-card ns-nbb-major-panel"
          style={{
            marginTop: 18,
            padding: 16,
            borderRadius: 14,
            border:
              "1px solid rgba(22,101,52,0.28)",
            background: "#f0fdf4",
          }}
        >
          {!applicationConfirmationOpen ? (
            <>
              <strong>
                {isFR
                  ? "Prêt à vérifier la modification du livre"
                  : "Ready to Review the Book Change"}
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin: "7px 0 14px",
                  lineHeight: 1.55,
                }}
              >
                {isFR
                  ? "La prochaine étape montre les conséquences exactes avant tout enregistrement."
                  : "The next step shows the exact consequences before anything is saved."}
              </p>

              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={
                  !proposal.ok ||
                  !proposal.readyToApply ||
                  divisionBusy
                }
                onClick={() => {
                  setApplicationError(
                    ""
                  );

                  setApplicationConfirmationOpen(
                    true
                  );
                }}
              >
                {isFR
                  ? "Réviser la division"
                  : "Review Division"}
              </button>
            </>
          ) : (
            <form
              role="dialog"
              aria-modal="true"
              aria-labelledby="apply-manuscript-division-title"
              aria-describedby="apply-manuscript-division-description"
              onSubmit={
                confirmDivisionApplication
              }
            >
              <h3
                id="apply-manuscript-division-title"
                style={{
                  margin: "0 0 8px",
                }}
              >
                {isFR
                  ? "Appliquer cette division ?"
                  : "Apply This Division?"}
              </h3>

              <p
                id="apply-manuscript-division-description"
                className="ns-card-meta"
                style={{
                  margin: "0 0 10px",
                  lineHeight: 1.6,
                }}
              >
                {isFR
                  ? `Cette action remplacera la page ou double page actuelle par ${segments.length} Pages simples. Le Plan du livre, le Plan du manuscrit et le Plan des illustrations seront mis à jour ensemble. Le texte${structuredSourceReview ? " et la mise en forme" : ""} du manuscrit seront préservés. Cette action sera enregistrée immédiatement.`
                  : `This will replace the current page or spread with ${segments.length} Single Pages. The Book Structure, Manuscript Plan, and Illustration Plan will be updated together. Your manuscript wording${structuredSourceReview ? " and formatting" : ""} will be preserved. This action will be saved immediately.`}
              </p>

              <p
                className="ns-card-meta"
                style={{
                  margin: "0 0 10px",
                  lineHeight: 1.6,
                }}
              >
                {isFR
                  ? "L’unité combinée d’origine ne restera plus une seule page ou double page."
                  : "The original combined unit will no longer remain as one page or spread."}
              </p>

              {selectedIllustrationDestination ? (
                <p
                  style={{
                    margin: "0 0 12px",
                    fontWeight: 800,
                    color: "#1e3a8a",
                  }}
                >
                  {isFR
                    ? `L’illustration existante et ses instructions resteront avec ${selectedIllustrationDestination}.`
                    : `The existing illustration and its directions will remain with ${selectedIllustrationDestination}.`}
                </p>
              ) : null}

              {applicationError ? (
                <p
                  role="alert"
                  style={{
                    margin: "0 0 12px",
                    padding: 12,
                    borderRadius: 10,
                    background: "#fff7f7",
                    border:
                      "1px solid rgba(220,38,38,0.28)",
                    color: "#991b1b",
                    lineHeight: 1.55,
                  }}
                >
                  {applicationError}
                </p>
              ) : null}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    divisionBusy
                  }
                  onClick={() => {
                    setApplicationConfirmationOpen(
                      false
                    );

                    setApplicationError(
                      ""
                    );
                  }}
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button ns-button-primary"
                  type="submit"
                  disabled={
                    divisionBusy
                  }
                  aria-busy={
                    divisionBusy
                  }
                >
                  {divisionBusy
                    ? (
                        isFR
                          ? "Enregistrement…"
                          : "Saving…"
                      )
                    : (
                        isFR
                          ? "Appliquer la division"
                          : "Apply Division"
                      )}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <div
          data-testid="division-read-only-application-notice"
          className="ns-card ns-nbb-major-panel"
          role="status"
          style={{
            marginTop: 18,
            padding: 16,
            borderRadius: 14,
            border:
              "1px solid rgba(180,83,9,0.28)",
            background: "#fff7ed",
          }}
        >
          <strong>
            {isFR
              ? "Application indisponible en mode lecture seule"
              : "Applying Is Unavailable in Read-only Mode"}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Vous pouvez réviser les pages proposées maintenant. Pour appliquer et enregistrer la division, rouvrez ce projet lorsque l’enregistrement est activé."
              : "You can review the proposed pages now. To apply and save the division, reopen this project when saving is enabled."}
          </p>
        </div>
      )}

      <div
        className="ns-card"
        style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 14,
          background: "#f8fafc",
          border:
            "1px solid #cbd5e1",
        }}
      >
        <strong>
          {isFR
            ? "Aucune modification n’a encore été appliquée"
            : "Nothing Has Been Applied Yet"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "7px 0 0",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "Cette révision n’a pas modifié le Plan du livre ni le Plan du manuscrit."
            : "This review has not changed your Book Structure or Manuscript Plan."}
        </p>
      </div>
    </section>
  );
}
